/**
 * Helpers de display para los modales de IA: convierten códigos
 * técnicos (`projectLanguage = "es"`, `profile_name = "openai_azure_full"`)
 * en strings amigables al usuario final ("Español", "Azure OpenAI").
 *
 * Mantenidos en lib/ porque se usan desde varios componentes
 * (SectionDraftDialog, GenerateAllDialog, ProposeStructureDialog).
 */

import type { TFunction } from 'i18next'

/** Convierte un código de idioma ISO-2 al nombre del idioma traducido
 * en el namespace `common.languages`. Defaultea al código si la key no
 * existe (ej. códigos no soportados como `pt`, `fr`). */
export function formatLanguage(code: string, t: TFunction): string {
  if (!code) return ''
  return t(`languages.${code}`, {
    ns: 'common',
    defaultValue: code,
  })
}

/** Convierte un slug de profile (ej. `openai_azure_full`) a un display
 * name humano. Si el slug no está en la tabla, hace un fallback
 * decente (snake_case → Title Case) en lugar de mostrar el slug raw.
 *
 * El profile NO es lo mismo que el provider: un profile combina rol
 * (reasoning, long_form, heavy_reasoning) con perfil de uso (full, fast,
 * lite). Aquí decidimos mostrar SOLO el provider para no abrumar al
 * user con detalles internos que no le aportan. */
export function formatProfileLabel(slug: string): string {
  if (!slug) return ''
  // Mapping explícito para slugs conocidos.
  const known: Record<string, string> = {
    openai_azure_full: 'Azure OpenAI',
    openai_azure_lite: 'Azure OpenAI',
    anthropic_full: 'Anthropic Claude',
    anthropic_lite: 'Anthropic Claude',
    voyage_full: 'VoyageAI',
  }
  if (slug in known) return known[slug]!
  // Fallback heurístico por prefijo del provider.
  if (slug.startsWith('openai_azure')) return 'Azure OpenAI'
  if (slug.startsWith('anthropic')) return 'Anthropic Claude'
  if (slug.startsWith('voyage')) return 'VoyageAI'
  if (slug.startsWith('ollama')) return 'Ollama (local)'
  // Último recurso: snake_case → Title Case ("foo_bar_baz" → "Foo Bar Baz").
  return slug
    .split('_')
    .map((part) =>
      part.length > 0 ? part[0]!.toUpperCase() + part.slice(1) : part,
    )
    .join(' ')
}
